import PropTypes from "prop-types"
import { useCallback, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { setThumbnailLoading } from "areas/product/store/actions"
import { openModal } from "components/modal"
import Button from "components/ui/button"
import Image from "components/ui/image"
import { isTestMode } from "helpers/application"
import { getImgSrcSet } from "helpers/image"
import { cn } from "styles/utils"

import ImageLoader from "./image-loader"

const getThumbnailSrcSet = (previewImage) =>
  previewImage.raw_src
    ? getImgSrcSet(previewImage.raw_src, 240, {
        dim_key: "dim",
        sizes: [80, 160],
      })
    : previewImage.srcSet

const PreviewThumbnail = ({ previewImage }) => {
  const wizardLoading = useSelector((state) => state.wizard.loading)
  const thumbnailLoading = useSelector(
    (state) => state.previewImageUI.thumbnailLoading
  )
  const [currentSrc, setCurrentSrc] = useState(previewImage.src)
  const [currentSrcSet, setCurrentSrcSet] = useState(
    getThumbnailSrcSet(previewImage)
  )
  const dispatch = useDispatch()

  const handlePreviewClick = useCallback(() => {
    try {
      if (!isTestMode) {
        dataLayer.push({
          event: "clicked-enlarge-preview-button",
        })
      }
    } catch (e) {
      console.log("Error sending clicked-enlarge-preview-button event", e)
    }

    openModal("#preview")
  }, [])

  useEffect(() => {
    if (!previewImage) return null

    const img = new window.Image()
    img.src = previewImage.src
    img.sizes = "80px"
    const srcSet = getThumbnailSrcSet(previewImage)
    if (srcSet) img.srcset = srcSet

    let cancelled = false

    img.onload = () => {
      if (!cancelled) {
        setCurrentSrc(previewImage.src)
        setCurrentSrcSet(srcSet)
        dispatch(setThumbnailLoading(false))
      }
    }

    img.onerror = () => {
      if (!cancelled) {
        dispatch(setThumbnailLoading(false))
      }
    }

    return () => {
      cancelled = true
      img.onload = null
      img.onerror = null
    }
  }, [previewImage])

  return (
    <Button
      className={cn(
        "border-grey-10 relative h-60 w-80 min-w-80 shrink-0 overflow-hidden border",
        (wizardLoading || thumbnailLoading) && "bg-white opacity-50"
      )}
      onClick={handlePreviewClick}
    >
      <Image
        alt="Product preview"
        className="h-full w-full object-contain"
        height="240"
        sizes="80px"
        src={currentSrc}
        srcSet={currentSrcSet}
        width="240"
      />
      <span className="text-10 absolute bottom-0 left-0 whitespace-nowrap px-4 py-2 text-center text-black">
        Click to enlarge
      </span>
      {(wizardLoading || thumbnailLoading) && (
        <ImageLoader className="size-12" />
      )}
    </Button>
  )
}

PreviewThumbnail.propTypes = {
  previewImage: PropTypes.object,
}

export default PreviewThumbnail
